/**
 * Global lead-document upload queue — survives navigation away from the lead
 * workspace. One batch per enqueue call; files upload sequentially with
 * resumable Storage progress for ETA and per-file status.
 */

import { uploadLeadDocumentWithProgress } from './leadDocumentService';
import type { LeadDocument } from '../types';

export type LeadUploadFileStatus = 'pending' | 'uploading' | 'complete' | 'error';

export interface LeadUploadFileItem {
  id: string;
  fileName: string;
  fileSize: number;
  status: LeadUploadFileStatus;
  bytesTransferred: number;
  error?: string;
  /** Populated when upload + Firestore write succeed — avoids refetching the full list. */
  completedDocument?: LeadDocument;
}

export type LeadUploadBatchStatus = 'running' | 'complete' | 'error';

export interface LeadUploadBatch {
  batchId: string;
  leadId: string;
  leadLabel: string;
  dealId?: string;
  uploadedBy: string;
  startedAt: number;
  finishedAt?: number;
  status: LeadUploadBatchStatus;
  files: LeadUploadFileItem[];
  totalBytes: number;
}

type Listener = (batches: LeadUploadBatch[]) => void;

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '—';
  if (seconds < 60) return `${Math.max(1, Math.round(seconds))} sec`;
  if (seconds < 3600) {
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return s > 0 ? `${m} min ${s} sec` : `${m} min`;
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return m > 0 ? `${h} hr ${m} min` : `${h} hr`;
}

export function getBatchTransferredBytes(batch: LeadUploadBatch): number {
  return batch.files.reduce((sum, f) => {
    if (f.status === 'complete') return sum + f.fileSize;
    if (f.status === 'uploading') return sum + f.bytesTransferred;
    return sum;
  }, 0);
}

export function getBatchProgressPct(batch: LeadUploadBatch): number {
  if (batch.totalBytes <= 0) return 0;
  return Math.min(100, (getBatchTransferredBytes(batch) / batch.totalBytes) * 100);
}

export function getBatchEtaSeconds(batch: LeadUploadBatch): number | null {
  if (batch.status !== 'running') return null;
  const elapsed = (Date.now() - batch.startedAt) / 1000;
  if (elapsed < 2) return null;
  const transferred = getBatchTransferredBytes(batch);
  if (transferred <= 0) return null;
  const rate = transferred / elapsed;
  if (rate <= 0) return null;
  const remaining = batch.totalBytes - transferred;
  return remaining / rate;
}

export function formatBatchEta(batch: LeadUploadBatch): string {
  const eta = getBatchEtaSeconds(batch);
  if (eta == null) return 'Calculating…';
  return `~${formatDuration(eta)} left`;
}

export function formatBatchSummary(batch: LeadUploadBatch): string {
  const done = batch.files.filter(f => f.status === 'complete').length;
  const total = batch.files.length;
  const transferred = getBatchTransferredBytes(batch);
  return `${done}/${total} files · ${formatBytes(transferred)} / ${formatBytes(batch.totalBytes)}`;
}

const PROGRESS_NOTIFY_MS = 400;

class LeadDocumentUploadManager {
  private batches: LeadUploadBatch[] = [];
  private listeners = new Set<Listener>();
  /** Raw File objects keyed by batch file id — kept until upload starts. */
  private fileHandles = new Map<string, File>();
  private progressNotifyTimer: ReturnType<typeof setTimeout> | null = null;

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.getBatches());
    return () => this.listeners.delete(listener);
  }

  getBatches(): LeadUploadBatch[] {
    return this.batches.map(b => ({
      ...b,
      files: b.files.map(f => ({ ...f })),
    }));
  }

  getActiveBatches(): LeadUploadBatch[] {
    return this.getBatches().filter(b => b.status === 'running');
  }

  isUploadingForLead(leadId: string): boolean {
    return this.batches.some(b => b.leadId === leadId && b.status === 'running');
  }

  getBatchForLead(leadId: string): LeadUploadBatch | undefined {
    return this.getBatches().find(b => b.leadId === leadId && b.status === 'running')
      ?? this.getBatches().filter(b => b.leadId === leadId).sort((a, b) => b.startedAt - a.startedAt)[0];
  }

  /** Fire-and-forget — uploads continue if the caller unmounts or navigates away. */
  enqueue(
    leadId: string,
    leadLabel: string,
    files: File[],
    uploadedBy: string,
    dealId?: string,
  ): string {
    const batchId = `batch-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const fileItems: LeadUploadFileItem[] = files.map((file, i) => {
      const id = `${batchId}-file-${i}`;
      this.fileHandles.set(id, file);
      return {
        id,
        fileName: file.name,
        fileSize: file.size,
        status: 'pending',
        bytesTransferred: 0,
      };
    });

    const batch: LeadUploadBatch = {
      batchId,
      leadId,
      leadLabel,
      dealId,
      uploadedBy,
      startedAt: Date.now(),
      status: 'running',
      files: fileItems,
      totalBytes: files.reduce((s, f) => s + f.size, 0),
    };

    this.batches.unshift(batch);
    this.notify();
    void this.runBatch(batchId);
    return batchId;
  }

  dismissBatch(batchId: string): void {
    const batch = this.batches.find(b => b.batchId === batchId);
    if (!batch || batch.status === 'running') return;
    this.batches = this.batches.filter(b => b.batchId !== batchId);
    this.notify();
  }

  private notify(): void {
    const snapshot = this.getBatches();
    this.listeners.forEach(fn => fn(snapshot));
  }

  private flushNotify(): void {
    if (this.progressNotifyTimer) {
      clearTimeout(this.progressNotifyTimer);
      this.progressNotifyTimer = null;
    }
    this.notify();
  }

  /** Coalesce high-frequency byte progress into ~2–3 UI updates per second. */
  private scheduleProgressNotify(): void {
    if (this.progressNotifyTimer) return;
    this.progressNotifyTimer = setTimeout(() => {
      this.progressNotifyTimer = null;
      this.notify();
    }, PROGRESS_NOTIFY_MS);
  }

  private updateFile(batchId: string, fileId: string, patch: Partial<LeadUploadFileItem>): void {
    const batch = this.batches.find(b => b.batchId === batchId);
    if (!batch) return;
    const file = batch.files.find(f => f.id === fileId);
    if (!file) return;
    Object.assign(file, patch);

    const isProgressTick =
      patch.bytesTransferred !== undefined &&
      (patch.status === undefined || patch.status === 'uploading') &&
      patch.completedDocument === undefined &&
      patch.error === undefined;

    if (isProgressTick) {
      this.scheduleProgressNotify();
    } else {
      this.flushNotify();
    }
  }

  private async runBatch(batchId: string): Promise<void> {
    const batch = this.batches.find(b => b.batchId === batchId);
    if (!batch) return;

    let hadError = false;

    for (const item of batch.files) {
      const file = this.fileHandles.get(item.id);
      if (!file) {
        this.updateFile(batchId, item.id, { status: 'error', error: 'File reference lost' });
        hadError = true;
        continue;
      }

      this.updateFile(batchId, item.id, { status: 'uploading', bytesTransferred: 0 });

      try {
        const completedDocument = await uploadLeadDocumentWithProgress(
          batch.leadId,
          file,
          batch.uploadedBy,
          batch.dealId,
          (bytesTransferred, totalBytes) => {
            this.updateFile(batchId, item.id, {
              status: 'uploading',
              bytesTransferred: Math.min(bytesTransferred, totalBytes),
            });
          },
        );
        this.updateFile(batchId, item.id, {
          status: 'complete',
          bytesTransferred: file.size,
          completedDocument,
        });
      } catch (err) {
        console.error('[LeadUpload] Failed:', item.fileName, err);
        this.updateFile(batchId, item.id, {
          status: 'error',
          error: err instanceof Error ? err.message : 'Upload failed',
        });
        hadError = true;
      } finally {
        this.fileHandles.delete(item.id);
      }
    }

    batch.status = hadError ? 'error' : 'complete';
    batch.finishedAt = Date.now();
    this.flushNotify();
  }
}

export const leadDocumentUploadManager = new LeadDocumentUploadManager();

export { formatBytes as formatUploadBytes, formatDuration as formatUploadDuration };
